import { useMutation } from "@tanstack/react-query";
import { getApiInstance } from "../utils/api";

const api = getApiInstance();

export type QRKeyData = {
  created_at?: string;
  key?: string;
  message?: string;
  subscription?: number | null;
  service?: number;
  is_used?: boolean;
  points_spent?: number;
  subscription_info?: {
    id: number;
    size: "motorcycle" | "small" | "medium" | "large" | "extra_large";
  } | null;
  service_info?: {
    id: number;
    name: string;
  } | null;
  service_size_info?: {
    id: number;
    size: "motorcycle" | "small" | "medium" | "large" | "extra_large";
  } | null;
  subscription_size_info?: {
    id: number;
    size: "motorcycle" | "small" | "medium" | "large" | "extra_large";
  } | null;
};

export type QRKeyResponse = {
  status: "success" | "error";
  message: string;
  data?: any; // سنحتفظ بالـ response الكامل هنا
};

/**
 * دالة مساعدة لاستخراج رسالة الخطأ من استجابة الباك اند
 */
const extractErrorMessage = (error: any): string => {
  // محاولة استخراج رسالة الخطأ من استجابة الباك اند
  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  // معالجة حقل non_field_errors (شائع في Django REST Framework)
  if (
    error?.response?.data?.non_field_errors &&
    Array.isArray(error.response.data.non_field_errors)
  ) {
    const nonFieldErrors = error.response.data.non_field_errors;
    if (nonFieldErrors.length > 0) {
      return nonFieldErrors[0]; // نأخذ أول خطأ
    }
  }

  // معالجة أخطاء الحقول المحددة (مثل key, subscription, etc.)
  if (error?.response?.data) {
    const data = error.response.data;
    // البحث في الحقول المحددة للأخطاء
    const fieldErrors = Object.keys(data).filter(
      (key) =>
        key !== "non_field_errors" &&
        key !== "detail" &&
        key !== "message" &&
        key !== "error" &&
        Array.isArray(data[key]) &&
        data[key].length > 0
    );

    if (fieldErrors.length > 0) {
      const firstField = fieldErrors[0];
      const fieldErrorMessages = data[firstField];
      if (Array.isArray(fieldErrorMessages) && fieldErrorMessages.length > 0) {
        return fieldErrorMessages[0];
      }
    }
  }

  // البحث في الحقول المتداخلة
  if (error?.response?.data) {
    const data = error.response.data;
    // البحث في الحقول الشائعة للأخطاء
    const errorFields = ["error", "message", "detail", "description", "reason"];
    for (const field of errorFields) {
      if (data[field]) {
        return data[field];
      }
    }

    // إذا كان data عبارة عن string
    if (typeof data === "string") {
      return data;
    }
  }

  // رسائل افتراضية حسب نوع الخطأ
  if (error?.response?.status === 404) {
    return "الكود غير صالح أو غير موجود";
  }

  if (error?.response?.status === 400) {
    return "خطأ في الكود المدخل";
  }

  if (error?.response?.status === 403) {
    return "غير مصرح لك باستخدام هذا الكود";
  }

  if (error?.response?.status === 409) {
    return "تم استخدام هذا الكود مسبقاً";
  }

  if (error?.response?.status === 410) {
    return "انتهت صلاحية هذا الكود";
  }

  if (error?.response?.status >= 500) {
    return "خطأ في الخادم، حاول مرة أخرى لاحقاً";
  }

  // رسالة افتراضية
  return error?.message || "حدث خطأ غير متوقع";
};

/**
 * يتحقق فقط من الكود ويعيد تفاصيله دون استخدامه.
 */
export function useCheckQRKey() {
  return useMutation<QRKeyResponse, Error, string>({
    mutationFn: async (key: string) => {
      try {
        const response = await api.post("/store/qr-codes/check-user-qr-code/", {
          key,
        });
        return {
          status: "success",
          message: "تم التحقق من الكود",
          data: response.data,
        };
      } catch (error: any) {
        // استخراج رسالة الخطأ من الباك اند
        const errorMessage = extractErrorMessage(error);

        return {
          status: "error",
          message: errorMessage,
          data: error?.response?.data || null,
        };
      }
    },
  });
}

/**
 * يستخدم الكود فعليًا (خصم غسلة/استهلاك خدمة).
 */
export function useUseQRKey() {
  return useMutation<QRKeyResponse, Error, string>({
    mutationFn: async (key: string) => {
      try {
        const response = await api.post("/store/qr-codes/user-qr-code/", {
          key,
        });
        return {
          status: "success",
          message: "تم استخدام الكود بنجاح",
          data: response.data,
        };
      } catch (error: any) {
        // استخراج رسالة الخطأ من الباك اند
        const errorMessage = extractErrorMessage(error);

        return {
          status: "error",
          message: errorMessage,
          data: error?.response?.data || null,
        };
      }
    },
  });
}
